document.addEventListener("DOMContentLoaded", () => {
	// =======================================================================
	// SETUP
	// =======================================================================

	/**
	 * Ensure Google Maps API is loaded
	 */
	if (
		typeof google === "undefined" ||
		!google.maps ||
		!google.maps.importLibrary
	) {
		return;
	}

	/**
	 * The main element selector for the map(s).
	 * @type {string}
	 */
	const mapElement = ".interactive-map__element";

	/**
	 * Global variables for the map and markers.
	 * Save here for performance and to avoid re-loading libraries.
	 */
	let allMarkerData;
	let map;
	let clusterer;
	let libraries;
	let imagePath = '/wp-content/themes/breeze/dist/images/icons';
	let markerImage = `${imagePath}/map-pin-royal-blue.svg`;
	let advancedMarkerImage = `${imagePath}/map-pin.svg`;

	// =======================================================================
	// LOAD GOOGLE LIBRARIES ASYNC
	// =======================================================================

	/**
	 * Load the necessary Google Maps libraries.
	 */
	async function loadGoogleMapsLibraries() {
		// Only load once
		// if (Map && InfoWindow && Marker && AdvancedMarkerElement && LatLngBounds) return;
		const [
			{ Map, InfoWindow },
			{ Marker, AdvancedMarkerElement },
			{ LatLngBounds, ControlPosition },
			{ PlaceAutocompleteElement },
			{ Geocoder },
		] = await Promise.all([
			google.maps.importLibrary("maps"),
			google.maps.importLibrary("marker"),
			google.maps.importLibrary("core"),
			google.maps.importLibrary("places"),
			google.maps.importLibrary("geocoding"),
		]);
		libraries = {
			Map,
			InfoWindow,
			Geocoder,
			Marker,
			AdvancedMarkerElement,
			LatLngBounds,
			ControlPosition,
			PlaceAutocompleteElement,
			MarkerClusterer: markerClusterer.MarkerClusterer,
		};
	}

	/**
	 * Initialize the map(s) once the libraries are loaded.
	 */
	loadGoogleMapsLibraries()
		.then(() => {
			// Libraries loaded
			document.querySelectorAll(mapElement).forEach((el) => {
				new InteractiveMap(el);
			});
		})
		.catch((error) => {
			console.error("Error loading Google Maps libraries:", error);
		});

	// =======================================================================
	// MAIN FUNCTION TO KICK OFF THE MAP AND CLUSTERING
	// =======================================================================

	/**
	 * Our main function to initialize the map and clustering.
	 *
	 * @param {DOMElement} mapElement The map container element.
	 */
	function InteractiveMap(mapElement) {
		try {
			const defaultZoom = mapElement.getAttribute("data-zoom") || 12;
			const markerDataResult = getMarkerData(mapElement);
			const enableSearch = mapElement.getAttribute("data-enable-search") === "true";
			const addressFromURL = getAddressFromURL();
			const newMarkerImg = mapElement.getAttribute("data-marker-image");
			let bounds;

			// Save some global data
			allMarkerData = markerDataResult;
			bounds = getBoundsForMarkers(allMarkerData);
			map = createMap(mapElement, defaultZoom);
			clusterer = new libraries.MarkerClusterer({ map, renderer: markerClustererRenderer });

			// Marker image override from data attribute
			if (newMarkerImg && newMarkerImg === 'branded') markerImage = `${imagePath}/map-pin-branded.svg`;

			// Fit the map to the bounds of the markers
			fitMapToBounds(map, bounds, allMarkerData.data.length, defaultZoom);

			// Initial render of markers
			filterAndRenderMarkers();

			// If enabled, initialize the search box
			// If an address is present in the URL, zoom to that address
			if (enableSearch) initializeSearch(mapElement, defaultZoom);
			if (addressFromURL) zoomToAddress(addressFromURL);

			// Re-filter markers when the map becomes idle after panning/zooming
			map.addListener("idle", () => filterAndRenderMarkers());
		} catch (error) {
			console.error("Error initializing InteractiveMap:", error);
		}
	}

	// =======================================================================
	// MARKER CLUSTER RENDERER
	// =======================================================================

	/**
	 * Custom renderer for marker clusters.
	 * Creates SVG icons with different colors based on cluster size.
	 */
	const markerClustererRenderer = {
		render: ({ count, position }, stats) => {
			const max = stats.clusters.markers.max;
			let color, size;

			// Determine color and size based on count
			if (count <= max / 3) {
				color = "#0099F3"; // Low Range (Electric Blue)
				size = 50;
			} else if (count <= (max * 2) / 3) {
				color = "#0B2B90"; // Mid Range (Royal Blue)
				size = 50;
			} else {
				color = "#030C28"; // High Range (Dark Blue)
				size = 50;
			}

			// create svg literal with fill color and dynamic size
			const svg = `<svg fill="${color}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240" width="${size}" height="${size}">
				<circle cx="120" cy="120" opacity=".8" r="70" />
				<circle cx="120" cy="120" opacity=".6" r="90" />
				<circle cx="120" cy="120" opacity=".3" r="110" />
				<text x="50%" y="50%" style="fill:#fff" text-anchor="middle" font-size="50" dominant-baseline="middle" font-family="roboto,arial,sans-serif">${count}</text>
			</svg>`;

			const title = `Cluster of ${count} markers`,
			// adjust zIndex to be above other markers
			zIndex = Number(google.maps.Marker.MAX_ZINDEX) + count;

			const clusterOptions = {
				position,
				zIndex,
				title,
				icon: {
					url: `data:image/svg+xml;base64,${btoa(svg)}`,
					anchor: new google.maps.Point(size / 2, size / 2),
				},
			};

			return new libraries.Marker(clusterOptions);
		},
	};

	// =======================================================================
	// ADDRESS AND SEARCH FUNCTIONS
	// =======================================================================

	/**
	 * Gets the address from the URL parameter.
	 *
	 * @returns {string|boolean} The address from the URL parameter or false if not present.
	 */
	function getAddressFromURL() {
		const urlParams = new URLSearchParams(window.location.search);
		return urlParams.get("address") || false;
	}

	/**
	 * Zooms the map to the specified address.
	 *
	 * @param {string} address
	 */
	function zoomToAddress(address) {
		const geocoder = new libraries.Geocoder();
		const isZipCode = /^\d{5}$/.test(address);

		geocoder.geocode({ address }, (results, status) => {
			if (status === "OK" && results[0]) {
				map.setCenter(results[0].geometry.location);
				map.setZoom(isZipCode ? 12 : 14);
			} else {
				console.error(
					"Geocode was not successful for the following reason: " +
						status
				);
			}
		});
	}

	/**
	 * Initializes the search functionality.
	 *
	 * @param {string} defaultZoom
	 */
	function initializeSearch(mapContainer, defaultZoom) {
		const placeAutocomplete = new libraries.PlaceAutocompleteElement();

		placeAutocomplete.id = "place-autocomplete-input";
		// Use the actual input element (new Maps API) or fall back to the element itself.
		const placeholderTarget =
			placeAutocomplete.inputElement || placeAutocomplete;
		if (placeholderTarget?.setAttribute) {
			placeholderTarget.setAttribute(
			"placeholder",
			"Search for address..."
		);
		}
		// Keep the control inside the map container to avoid duplicate layout space.
		if (mapContainer?.appendChild) {
			mapContainer.appendChild(placeAutocomplete);
		}
		map.controls[libraries.ControlPosition.TOP_LEFT].push(
			placeAutocomplete
		);

		// Bias the SearchBox results towards current map's viewport.
		map.addListener("bounds_changed", () => {
			placeAutocomplete.locationRestriction = map.getBounds();
		});

		// Add the gmp-placeselect listener, and display the results.
		placeAutocomplete.addEventListener(
			"gmp-select",
			async ({ placePrediction }) => {
				const place = placePrediction.toPlace();
				await place.fetchFields({
					fields: ["displayName", "formattedAddress", "location"],
				});

				// If the place has a geometry, then present it on a map.
				if (place.viewport) {
					map.fitBounds(place.viewport);
				} else {
					map.setCenter(place.location);
				}

				map.setZoom(defaultZoom);
			}
		);
	}

	// =======================================================================
	// THE MAP HANDLING FUNCTIONS
	// - MARKERS, BOUNDS, CLUSTERING, ETC.
	// =======================================================================

	/**
	 * Creates a Google Map instance.
	 *
	 * @param {DOMElement} mapElement
	 * @param {string} defaultZoom
	 * @returns {libraries.Map}
	 */
	function createMap(mapElement, defaultZoom) {
		return new libraries.Map(mapElement, {
			mapId: "2d358a6df0bdc6efc9df65d7", // custom styled map (controled in cloud console)
			zoom: parseInt(defaultZoom), // default zoom level
			disableDefaultUI:
				mapElement.getAttribute("data-disable-default-ui") === "true", // disable all default UI
			clickableIcons: false, // citys, POIs, etc. are not clickable
			mapTypeControl: false, // always disable map/satellite toggle
			streetViewControl: false, // always disable street view button
		});
	}

	/**
	 * Gets the marker data from the map element.
	 *
	 * @param {DOMElement} mapElement
	 * @returns {Object} Marker data and library
	 */
	function getMarkerData(mapElement) {
		// If using the jobs data source, we expect a global `jobs` variable to be present
		if (typeof jobs !== "undefined" && Array.isArray(jobs)) {
			return { data: jobs, library: libraries.Marker };
			// Otherwise, we expect marker data JSON encoded in a data attribute
		} else {
			return {
				data: JSON.parse(mapElement.getAttribute("data-markers")) || [],
				library: libraries.AdvancedMarkerElement,
			};
		}
	}

	/**
	 * Gets the bounds for the markers.
	 *
	 * @param {Object} markerData
	 * @returns {libraries.LatLngBounds}
	 */
	function getBoundsForMarkers(markerData) {
		const bounds = new libraries.LatLngBounds();
		markerData.data.forEach((marker) => {
			bounds.extend({
				lat: parseFloat(marker.lat),
				lng: parseFloat(marker.lng),
			});
		});
		return bounds;
	}

	/**
	 * Fits the map to the bounds of the markers.
	 *
	 * @param {libraries.Map} map
	 * @param {libraries.LatLngBounds} bounds
	 * @param {number} markerCount
	 * @param {string} defaultZoom
	 */
	function fitMapToBounds(map, bounds, markerCount, defaultZoom) {
		if (markerCount === 1 && defaultZoom) {
			map.setCenter(bounds.getCenter());
			map.setZoom(parseInt(defaultZoom));
		} else if (markerCount > 100) {
			map.fitBounds(bounds, 50);
		} else {
			map.fitBounds(bounds);
		}
	}

	/**
	 * Filters and renders markers on the map based on the current bounds.
	 *
	 * Note on marker types:
	 * - Marker: More performant, but less flexible styling options.
	 * - AdvancedMarkerElement: More flexible styling options, but less performant.
	 *
	 * We choose the marker type based on the data source.
	 * If using the jobs data source, we use Marker for performance.
	 * If using custom uploaded data, we use AdvancedMarkerElement for flexibility.
	 *
	 * For more info on AdvancedMarkerElement performance issues:
	 * @link https://issuetracker.google.com/issues/333423690#comment17
	 * @link https://issuetracker.google.com/issues/333423690#comment35
	 *
	 * @returns {void}
	 */
	function filterAndRenderMarkers() {
		const bounds = map.getBounds();
		const MarkerLibrary = allMarkerData.library;

		if (!bounds) return;

		// Determine which markers are within the current bounds
		const visibleMarkerData = allMarkerData.data.filter((markerData) => {
			const markerPosition = new google.maps.LatLng(
				parseFloat(markerData.lat),
				parseFloat(markerData.lng)
			);
			return bounds.contains(markerPosition);
		});

		// Create new markers but do not add to map yet (done by clusterer)
		const newMarkers = visibleMarkerData.map((markerData) => {
			const marker = new MarkerLibrary({
				position: {
					lat: parseFloat(markerData.lat),
					lng: parseFloat(markerData.lng),
				},
				title:
					markerData.name && markerData.name.length
						? markerData.name
						: "",
			});

			// Legacy Marker (used only for complete jobs map)
			// - More performant for large number of markers
			// - No InfoWindow
			if (MarkerLibrary === libraries.Marker) {
				marker.setIcon(markerImage);

			// AdvancedMarkerElement (should only be used for less than 20 markers ideally)
			// - Activate InfoWindow on click
			} else if (MarkerLibrary === libraries.AdvancedMarkerElement) {
				marker.content = (() => {
					const pinImage = document.createElement("img");
					pinImage.src = advancedMarkerImage;
					return pinImage;
				})();
				marker.gmpClickable = true;

				const infoWindow = new libraries.InfoWindow();
				marker.addListener("click", ({ domEvent, latLng }) => {
					const { target } = domEvent;
					infoWindow.close();
					infoWindow.setContent(marker.title);
					infoWindow.open(marker.map, marker);
				});
			}

			return marker;
		});

		// Clear existing markers from the clusterer and add new ones for the current bounds
		clusterer.clearMarkers();
		clusterer.addMarkers(newMarkers);
	}
});
